import { Content, SaveContent } from "@/types/content";
import { Generation } from "@/types/design";
import { ServerError } from "@/types/errors";
import { PayOrder } from "@/types/integral";
import { UserProfile } from "@/types/user";

export enum LogLevel {
  Error = "ERROR",
  Warning = "WARNING",
  Info = "INFO",
  Debug = "DEBUG",
}

export type ClientResponse<T> = {
  response: Response;
  headers: Map<string, string>;
  data: T;
};

export type Options = {
  headers?: { [x: string]: string };
  method?: string;
  url?: string;
  credentials?: "omit" | "same-origin" | "include";
  body?: any;
};

export type StatusOK = {
  status: "OK";
};
export function parseAndMergeNestedHeaders(originalHeaders: any) {
  const headers = new Map();
  let nestedHeaders = new Map();
  originalHeaders.forEach((val: string, key: string) => {
    const capitalizedKey = key.replace(/\b[a-z]/g, (l) => l.toUpperCase());
    let realVal = val;
    if (val && val.match(/\n\S+:\s\S+/)) {
      const nestedHeaderStrings = val.split("\n");
      realVal = nestedHeaderStrings.shift() as string;
      const moreNestedHeaders = new Map(nestedHeaderStrings.map((h: any) => h.split(/:\s/)));
      nestedHeaders = new Map([...nestedHeaders, ...moreNestedHeaders]);
    }
    headers.set(capitalizedKey, realVal);
  });
  return new Map([...headers, ...nestedHeaders]);
}
export function cleanUrlForLogging(baseUrl: string, apiUrl: string): string {
  let url = apiUrl;

  // Trim the host name
  url = url.substring(baseUrl.length);

  // Filter the query string
  const index = url.indexOf("?");
  if (index !== -1) {
    url = url.substring(0, index);
  }

  // A non-exhaustive whitelist to exclude parts of the URL that are unimportant (eg IDs) or may be sentsitive
  // (eg email addresses). We prefer filtering out fields that aren't recognized because there should generally
  // be enough left over for debugging.
  //
  // Note that new API routes don't need to be added here since this shouldn't be happening for newly added routes.
  const whitelist = ["api", "v4", "users", "teams", "scheme", "name", "members", "channels", "posts", "reactions", "commands", "files", "preferences", "hooks", "incoming", "outgoing", "oauth", "apps", "emoji", "brand", "image", "data_retention", "jobs", "plugins", "roles", "system", "timezones", "schemes", "redirect_location", "patch", "mfa", "password", "reset", "send", "active", "verify", "terms_of_service", "login", "logout", "ids", "usernames", "me", "username", "email", "default", "sessions", "revoke", "all", "audits", "device", "status", "search", "switch", "authorized", "authorize", "deauthorize", "tokens", "disable", "enable", "exists", "unread", "invite", "batch", "stats", "import", "schemeRoles", "direct", "group", "convert", "view", "search_autocomplete", "thread", "info", "flagged", "pinned", "pin", "unpin", "opengraph", "actions", "thumbnail", "preview", "link", "delete", "logs", "ping", "config", "client", "license", "websocket", "webrtc", "token", "regen_token", "autocomplete", "execute", "regen_secret", "policy", "type", "cancel", "reload", "environment", "s3_test", "file", "caches", "invalidate", "database", "recycle", "compliance", "reports", "cluster", "ldap", "test", "sync", "saml", "certificate", "public", "private", "idp", "elasticsearch", "purge_indexes", "analytics", "old", "webapp", "fake"];

  url = url
    .split("/")
    .map((part) => {
      if (part !== "" && whitelist.indexOf(part) === -1) {
        return "<filtered>";
      }

      return part;
    })
    .join("/");

  if (index !== -1) {
    // Add this on afterwards since it wouldn't pass the whitelist
    url += "?<filtered>";
  }

  return url;
}
export class ClientError extends Error implements ServerError {
  url?: string;
  server_error_id?: string;
  status_code?: number;

  constructor(baseUrl: string, data: ServerError) {
    super(data.message + ": " + cleanUrlForLogging(baseUrl, data.url || ""));

    this.message = data.message;
    this.url = data.url;
    this.server_error_id = data.server_error_id;
    this.status_code = data.status_code;

    // Ensure message is treated as a property of this class when object spreading. Without this,
    // copying the object by using `{...error}` would not include the message.
    Object.defineProperty(this, "message", { enumerable: true });
  }
}

export function buildQueryString(parameters: Record<string, any>): string {
  const keys = Object.keys(parameters);
  if (keys.length === 0) {
    return "";
  }

  const queryParams = Object.entries(parameters)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  return queryParams.length > 0 ? `?${queryParams}` : "";
}

const HEADER_REQUESTED_WITH = "X-Requested-With";
const HEADER_AUTH = "Authorization";
const HEADER_BEARER = "BEARER";
const HEADER_X_CSRF_TOKEN = "X-CSRF-Token";
const HEADER_USER_AGENT = "User-Agent";
const HEADER_CONTENT_TYPE = "Content-Type";
const HEADER_X_VERSION_ID = "X-Version-Id";

class Client {
  defaultHeaders: { [x: string]: string } = {};
  setAuthHeader = true;
  includeCookies = true;
  userAgent: string | null = null;
  token: string | null = "";
  csrf = "";
  logToConsole = false;
  serverVersion = "";
  url = window.location.origin;
  urlVersion = "/api/v4";

  getUrl() {
    return this.url;
  }

  getBaseRoute() {
    return `${this.url}${this.urlVersion}`;
  }

  //新增内容管理
  getContentsRoute() {
    return `${this.getBaseRoute()}/contents`;
  }

  getCSRFFromCookie() {
    if (typeof document !== "undefined" && typeof document.cookie !== "undefined") {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith("MMCSRF=")) {
          return cookie.replace("MMCSRF=", "");
        }
      }
    }
    return "";
  }

  getOptions(options: Options) {
    const newOptions: Options = { ...options };
    const headers: { [x: string]: string } = {
      [HEADER_REQUESTED_WITH]: "XMLHttpRequest",
      ...this.defaultHeaders,
    };

    if (this.setAuthHeader) {
      this.token = localStorage.getItem("token");
      headers[HEADER_AUTH] = `${HEADER_BEARER} ${this.token}`;
    }

    const csrfToken = this.csrf || this.getCSRFFromCookie();
    if (options.method && options.method.toLowerCase() !== "get" && csrfToken) {
      headers[HEADER_X_CSRF_TOKEN] = csrfToken;
    }

    if (this.includeCookies) {
      newOptions.credentials = "include";
    }

    if (this.userAgent) {
      headers[HEADER_USER_AGENT] = this.userAgent;
    }

    if (!headers[HEADER_CONTENT_TYPE] && options.body) {
      // when the body is an instance of FormData we let browser set the Content-Type header generated by FormData interface with correct boundary
      if (!(options.body instanceof FormData)) {
        headers[HEADER_CONTENT_TYPE] = "application/json";
      }
    }

    if (newOptions.headers) {
      Object.assign(headers, newOptions.headers);
    }

    return {
      ...newOptions,
      headers,
    };
  }

  private doFetchWithResponse = async <ClientDataResponse>(url: string, options: Options): Promise<ClientResponse<ClientDataResponse>> => {
    const response = await fetch(url, this.getOptions(options));
    const headers = parseAndMergeNestedHeaders(response.headers);

    let data;
    try {
      data = await response.json();
    } catch (err) {
      throw new ClientError(this.getUrl(), {
        message: "Received invalid response from the server.",
        url,
      });
    }

    if (headers.has(HEADER_X_VERSION_ID) && !headers.get("Cache-Control")) {
      const serverVersion = headers.get(HEADER_X_VERSION_ID);
      if (serverVersion && this.serverVersion !== serverVersion) {
        this.serverVersion = serverVersion;
      }
    }

    if (response.ok) {
      return {
        response,
        headers,
        data,
      };
    }

    const msg = data.message || "";

    if (this.logToConsole) {
      console.error(msg); // eslint-disable-line no-console
    }

    throw new ClientError(this.getUrl(), {
      message: msg,
      server_error_id: data.id,
      status_code: data.status_code,
      url,
    });
  };

  protected doFetch = async <ClientDataResponse>(url: string, options: Options): Promise<ClientDataResponse> => {
    const { data } = await this.doFetchWithResponse<ClientDataResponse>(url, options);
    return data;
  };

  saveContent = (data: SaveContent) => {
    return this.doFetch<Content>(`${this.getContentsRoute()}`, {
      method: "post",
      body: JSON.stringify(data),
    });
  };
  getContents(page = 0, per_page = 60, type: string, userId: string) {
    return this.doFetch<Content[]>(`${this.getContentsRoute()}${buildQueryString({ page, per_page, type, userId })}`, {
      method: "get",
    });
  }
  deleteContent = (id: string) => {
    return this.doFetch<Content>(`${this.getContentsRoute()}/${id}/delete`, {
      method: "delete",
    });
  };

  // 生图支付
  postPay = (payOrder: PayOrder) => {
    return this.doFetch(`${this.getBaseRoute()}/payment`, { method: "post", body: JSON.stringify(payOrder) });
  };

  getMe = () => {
    return this.doFetch<UserProfile>(`${this.getBaseRoute()}/users/me`, {
      method: "get",
    });
  };

  postGenImage = (data: Generation) => {
    return this.doFetch<Generation>(`${this.getBaseRoute()}/generate`, {
      method: "post",
      body: JSON.stringify(data),
    });
  }
  getGenImage(taskId: string) {
    return this.doFetch<Generation>(`${this.getBaseRoute()}/generate?task_id=${taskId}`, {
      method: "get",
    });
  }
}

const Client4 = new Client();
export default Client4;
